import React from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare,
  User,
  Calendar,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Loader2,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { conversationService } from "../../services/conversationService";
import type { Conversation } from "../../types/conversation";
import { Card } from "../ui/Card";
import { userDisplay } from "../../lib/conversationDisplay";

interface RecentConversationsProps {
  limit?: number;
  showTitle?: boolean;
}

export function RecentConversations({
  limit = 5,
  showTitle = true,
}: RecentConversationsProps): JSX.Element {
  const navigate = useNavigate();

  const {
    data: conversationsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["conversations", "recent", limit],
    queryFn: () =>
      conversationService.getConversations({
        page: 0,
        size: limit,
      }),
    refetchOnMount: "always",
  });

  const formatDate = (dateString: string): string => {
    // Ensure the date string is treated as UTC if it doesn't have timezone info
    const utcDateString = dateString.endsWith("Z")
      ? dateString
      : dateString + "Z";
    const date = new Date(utcDateString);
    const now = new Date();

    // Calculate difference in milliseconds
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) {
      return "Just now";
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleViewConversation = (conversationId: string): void => {
    navigate(`/admin/conversations/${conversationId}`);
  };

  const handleViewAllConversations = (): void => {
    navigate("/admin/conversations");
  };

  return (
    <div className="space-y-4">
      {showTitle && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-brand-muted" />
            <h3 className="text-lg font-semibold text-brand-text">
              Recent Conversations
            </h3>
          </div>
          <button
            onClick={handleViewAllConversations}
            className="text-sm text-brand-primary hover:opacity-70 font-medium"
          >
            View all
          </button>
        </div>
      )}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-brand-border">
                <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                  User
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                  First Message
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                  Started
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                  Messages
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                  Feedback
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-brand-muted">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-4 text-center text-sm text-brand-muted"
                  >
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-muted" />
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-4 text-center text-sm text-brand-muted"
                  >
                    Failed to load conversations
                  </td>
                </tr>
              ) : conversationsData?.conversations.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-4 text-center text-sm text-brand-muted"
                  >
                    No recent conversations
                  </td>
                </tr>
              ) : (
                conversationsData?.conversations.map(
                  (conversation: Conversation) => (
                    <tr key={conversation.id} className="hover:bg-brand-surface">
                      <td className="px-4 py-3 text-sm text-brand-text">
                        <div className="flex items-center" title={userDisplay(conversation).tooltip}>
                          <User className="w-3 h-3 mr-2 text-brand-muted" />
                          {userDisplay(conversation).label}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-brand-muted max-w-xs">
                        <div
                          className="truncate"
                          title={
                            conversation.first_message_snippet || "No message"
                          }
                        >
                          {conversation.first_message_snippet || "No message"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-brand-muted whitespace-nowrap">
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1 text-brand-muted" />
                          {formatDate(conversation.started_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-brand-muted whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-pill text-xs font-medium bg-brand-teal/10 text-brand-teal">
                          {conversation.message_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-brand-muted whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            <ThumbsUp className="w-3 h-3 text-brand-teal" />
                            <span className="text-brand-teal text-xs font-medium">
                              {conversation.feedback_stats?.positive_count || 0}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <ThumbsDown className="w-3 h-3 text-brand-error" />
                            <span className="text-brand-error text-xs font-medium">
                              {conversation.feedback_stats?.negative_count || 0}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium">
                        <button
                          onClick={() =>
                            handleViewConversation(conversation.id)
                          }
                          className="text-brand-primary hover:opacity-70 p-1 rounded hover:bg-brand-surface"
                          title="View conversation"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
